import React, { useCallback, useState } from 'react'
import { usePersistedState } from '@cbgms/base/hooks/persisted-state/use-persisted-state'
import { useKeyBindings } from '@cbgms/base/keybinds'

export enum enumFilterPosition {
  'left',
  'right'
}

export const toggleShowAttributeHotKey = 'shift+f7'

interface IExpose {
  showProductGroupSearcher: boolean
  setShowProductGroupSearcher: (updatedShowNetPrice: boolean) => void
  filterColumnPosition: enumFilterPosition
  setFilterColumnPosition: (updatedValue: enumFilterPosition) => void
  hasBeenProvided: boolean
  toggleIsShowingAttributeSources: () => void
  isShowingAttributeSources: boolean
  isShowingHiddenAttributes: boolean
}

export const CatalogSettingsContext = React.createContext<IExpose>({
  showProductGroupSearcher: false,
  setShowProductGroupSearcher: () => {}, // this is overriden by the default provider with a persistedValue
  filterColumnPosition: enumFilterPosition.left,
  setFilterColumnPosition: () => {},
  hasBeenProvided: false,
  toggleIsShowingAttributeSources: () => {},
  isShowingAttributeSources: false,
  isShowingHiddenAttributes: false
})

export const CatalogSettingsContextProvider: React.FC<{}> = ({ children }) => {
  const { bindShortcut } = useKeyBindings()
  const [showProductGroupSearcher, setShowProductGroupSearcher] = usePersistedState('showProductGroupSearcher', false)
  const [filterColumnPosition, setFilterColumnPosition] = usePersistedState('filterColumnPosition', enumFilterPosition.left)

  // initially IsShowingAttributes  should be OFF, safety precaution to not have prod users accidently turned on
  const [isShowingAttributeSources, setIsShowingAttributeSources] = useState<boolean>(false)
  const toggleIsShowingAttributeSources = useCallback(() => setIsShowingAttributeSources(prev => !prev), [])
  React.useEffect(() => {
    bindShortcut(toggleShowAttributeHotKey, toggleIsShowingAttributeSources)
  }, [])

  const value = React.useMemo(() => {
    return {
      showProductGroupSearcher,
      setShowProductGroupSearcher,
      filterColumnPosition,
      setFilterColumnPosition,
      hasBeenProvided: true,
      isShowingAttributeSources,
      isShowingHiddenAttributes: isShowingAttributeSources,
      toggleIsShowingAttributeSources
    }
  }, [
    showProductGroupSearcher,
    setShowProductGroupSearcher,
    filterColumnPosition,
    setFilterColumnPosition,
    isShowingAttributeSources,
    toggleIsShowingAttributeSources
  ])

  return <CatalogSettingsContext.Provider value={value}>{children}</CatalogSettingsContext.Provider>
}

export function useCatalogSettingsContext() {
  const context = React.useContext(CatalogSettingsContext)
  if (context === undefined || !context.hasBeenProvided) {
    console.error('useCatalogSettingsContext must be used within an CatalogSettingsContext')
  }

  return context as IExpose
}

export const { Consumer: CatalogSettingsContextConsumer } = CatalogSettingsContext
